import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { ApplyFormData } from '../ApplyFormComponent1';
import { PhoneCodeSelect } from './PhoneCode/PhoneCodeSelect';
import { useLocation } from 'react-router-dom';

const generateRange = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

/**
 * メールアドレス専用のカスタムバリデーション
 * 条件
 * 1. 特殊文字 (/、!、"、#、$、%) を含むものはNG
 * 2. @が2つ以上含まれるものはNG
 * 3. @の直前が英数字以外
 * 4. '.'または'_'が2つ以上連続しているものはNG
 * 5. 最初の文字が英数字以外のものはNG
 */
const validateCustomEmail = (
  value: string,
  useEnglish: boolean
): true | string => {
  const errorsList: string[] = [];

  // (1) 特殊文字
  const forbiddenChars = /[!"#$%/]/; // スラッシュはエスケープ不要
  if (forbiddenChars.test(value)) {
    errorsList.push(
      useEnglish
        ? `Cannot contain these special characters (/!"#$%)`
        : `特殊文字 (/!"#$%) は使用できません`
    );
  }

  // (2) @ が2つ以上含まれる
  const atCount = (value.match(/@/g) || []).length;
  if (atCount >= 2) {
    errorsList.push(
      useEnglish
        ? `Cannot contain 2 or more '@' characters`
        : `@を2つ以上含むアドレスは無効です`
    );
  }

  // (3) @の直前が英数字以外
  const atIndex = value.indexOf('@');
  if (atIndex > 0) {
    const charBeforeAt = value[atIndex - 1];
    if (!/^[A-Za-z0-9]$/.test(charBeforeAt)) {
      errorsList.push(
        useEnglish
          ? `The character before '@' must be alphanumeric`
          : `@の直前は英数字である必要があります`
      );
    }
  }

  // (4) '.'または'_'が2つ以上連続
  if (/\.\.|__/.test(value)) {
    errorsList.push(
      useEnglish
        ? `Cannot have consecutive '..' or '__'`
        : `「..」または「__」を連続使用できません`
    );
  }

  // (5) 最初の文字が英数字以外
  if (!/^[A-Za-z0-9]/.test(value)) {
    errorsList.push(
      useEnglish
        ? `The first character must be alphanumeric`
        : `メールアドレスの最初の文字は英数字である必要があります`
    );
  }

  if (errorsList.length > 0) {
    return errorsList.join('\n');
  }
  return true;
};

export const ResidentInfoForm: React.FC = () => {
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
    control,
  } = useFormContext<ApplyFormData>();

  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const currentYear = new Date().getFullYear();
  const years = generateRange(1900, currentYear);
  const months = generateRange(1, 12);
  const days = generateRange(1, 31);

  // 「契約者情報を反映」ボタン押下で、契約者情報を入居者情報にコピー
  const handleReflectContractInfo = () => {
    const contract = getValues('contractData');
    setValue('residentData.firstName', contract.firstName || '');
    setValue('residentData.lastName', contract.lastName || '');
    setValue('residentData.firstNameKana', contract.firstNameKana || '');
    setValue('residentData.lastNameKana', contract.lastNameKana || '');
    setValue('residentData.country', contract.country || '');
    setValue('residentData.address', contract.address || '');
    setValue('residentData.birthYear', contract.birthYear || '');
    setValue('residentData.birthMonth', contract.birthMonth || '');
    setValue('residentData.birthDay', contract.birthDay || '');
    setValue('residentData.ResidentGender', contract.gender || '');
    setValue('residentData.email', contract.email || '');
    setValue('residentData.phoneNumber', contract.phoneNumber || '');
    // 続柄は "本人" 固定（日本語の場合）または "Self"（英語の場合）
    setValue('residentData.relation', useEnglish ? 'Self' : '本人');
  };

  const inputStyles =
    'w-full border border-black mt-1 h-[27px] text-sm px-1 py-0';
  const selectStyles =
    'w-full border border-black mt-1 h-[27px] text-sm px-1 py-0';
  const labelStyles = 'block text-gray-800 text-sm';

  return (
    <div className="w-full max-md:max-w-md max-md:mx-auto px-5 py-5 bg-[#f6f3e6] mt-5">
      {/* 契約者情報を反映ボタン */}
      <div className="mb-5">
        <button
          type="button"
          onClick={handleReflectContractInfo}
          className="bg-black text-white px-4 py-1 text-sm rounded hover:bg-black"
        >
          {useEnglish ? 'Reflect Contractor Info' : '契約者情報を反映'}
        </button>
      </div>

      {/* 姓・名 */}
      <div className="mb-5">
        <div className="flex space-x-4">
          {/* 姓 */}
          <div className="w-1/2">
            <label className={labelStyles}>
              {useEnglish ? 'Last Name' : '姓'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={useEnglish ? 'e.g. Tanaka' : '例）田中'}
                className={`${inputStyles} ${
                  errors.residentData?.lastName ? 'border-red-500' : ''
                }`}
                {...register('residentData.lastName', {
                  required: useEnglish
                    ? 'Last Name is required'
                    : '氏名（姓）が未入力です',
                })}
              />
              {errors.residentData?.lastName && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                  !
                </span>
              )}
            </div>
            {errors.residentData?.lastName && (
              <div className="text-red-500 text-sm mt-1">
                {errors.residentData.lastName.message?.toString()}
              </div>
            )}
          </div>

          {/* 名 */}
          <div className="w-1/2">
            <label className={labelStyles}>
              {useEnglish ? 'First Name' : '名'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={useEnglish ? 'e.g. Taro' : '例）太郎'}
                className={`${inputStyles} ${
                  errors.residentData?.firstName ? 'border-red-500' : ''
                }`}
                {...register('residentData.firstName', {
                  required: useEnglish
                    ? 'First Name is required'
                    : '氏名（名）が未入力です',
                })}
              />
              {errors.residentData?.firstName && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                  !
                </span>
              )}
            </div>
            {errors.residentData?.firstName && (
              <div className="text-red-500 text-sm mt-1">
                {errors.residentData.firstName.message?.toString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* フリガナ（セイ・メイ） */}
      <div className="mb-5">
        <div className="flex space-x-4">
          {/* セイ (フリガナ) */}
          <div className="w-1/2">
            <label className={labelStyles}>
              {useEnglish ? 'Last Name (Kana)' : 'セイ（フリガナ）'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={useEnglish ? 'e.g. TANAKA' : '例）タナカ'}
                className={`${inputStyles} ${
                  errors.residentData?.lastNameKana ? 'border-red-500' : ''
                }`}
                {...register('residentData.lastNameKana', {
                  required: useEnglish
                    ? 'Last Name (Kana) is required'
                    : 'フリガナ（姓）が未入力です',
                })}
              />
              {errors.residentData?.lastNameKana && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                  !
                </span>
              )}
            </div>
            {errors.residentData?.lastNameKana && (
              <div className="text-red-500 text-sm mt-1">
                {errors.residentData.lastNameKana.message?.toString()}
              </div>
            )}
          </div>

          {/* メイ (フリガナ) */}
          <div className="w-1/2">
            <label className={labelStyles}>
              {useEnglish ? 'First Name (Kana)' : 'メイ（フリガナ）'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={useEnglish ? 'e.g. TARO' : '例）タロウ'}
                className={`${inputStyles} ${
                  errors.residentData?.firstNameKana ? 'border-red-500' : ''
                }`}
                {...register('residentData.firstNameKana', {
                  required: useEnglish
                    ? 'First Name (Kana) is required'
                    : 'フリガナ（名）が未入力です',
                })}
              />
              {errors.residentData?.firstNameKana && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                  !
                </span>
              )}
            </div>
            {errors.residentData?.firstNameKana && (
              <div className="text-red-500 text-sm mt-1">
                {errors.residentData.firstNameKana.message?.toString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 契約者との続柄 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Relationship to Contractor' : '契約者との続柄'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder={useEnglish ? 'e.g. Self' : '例）本人'}
            className={`${inputStyles} ${
              errors.residentData?.relation ? 'border-red-500' : ''
            }`}
            {...register('residentData.relation', {
              required: useEnglish
                ? 'Relationship is required'
                : '続柄が未入力です',
            })}
          />
          {errors.residentData?.relation && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
              !
            </span>
          )}
        </div>
        {errors.residentData?.relation && (
          <div className="text-red-500 text-sm mt-1">
            {errors.residentData.relation.message?.toString()}
          </div>
        )}
      </div>

      {/* 国籍 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Nationality' : '国籍'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder={useEnglish ? 'e.g. Japan' : '例）日本'}
            className={`${inputStyles} ${
              errors.residentData?.country ? 'border-red-500' : ''
            }`}
            {...register('residentData.country', {
              required: useEnglish
                ? 'Nationality is required'
                : '国名を入力してください',
            })}
          />
          {errors.residentData?.country && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
              !
            </span>
          )}
        </div>
        {errors.residentData?.country && (
          <div className="text-red-500 text-sm mt-1">
            {errors.residentData.country.message?.toString()}
          </div>
        )}
      </div>

      {/* 住所 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Address' : '住所'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder={
              useEnglish
                ? 'e.g. 1-1 Sakura-cho, Shibuya-ku, Tokyo'
                : '例）東京都渋谷区桜丘町31-2'
            }
            className={`${inputStyles} ${
              errors.residentData?.address ? 'border-red-500' : ''
            }`}
            {...register('residentData.address', {
              required: useEnglish ? 'Address is required' : '住所が未入力です',
            })}
          />
          {errors.residentData?.address && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
              !
            </span>
          )}
        </div>
        {errors.residentData?.address && (
          <div className="text-red-500 text-sm mt-1">
            {errors.residentData.address.message?.toString()}
          </div>
        )}
      </div>

      {/* 生年月日 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Birthday' : '生年月日'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="flex space-x-2 mt-1">
          {/* 年 */}
          <div className="relative w-1/3">
            <select
              className={`${selectStyles} ${errors.residentData?.birthYear ? 'border-red-500' : ''}`}
              {...register('residentData.birthYear', {
                required: useEnglish
                  ? 'Year is required'
                  : '生年月日(年)が未選択です',
              })}
            >
              <option value="">{useEnglish ? 'Year' : '年'}</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {useEnglish ? year : `${year}年`}
                </option>
              ))}
            </select>
            {errors.residentData?.birthYear && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-2">
                !
              </span>
            )}
          </div>
          {/* 月 */}
          <div className="relative w-1/3">
            <select
              className={`${selectStyles} ${errors.residentData?.birthMonth ? 'border-red-500' : ''}`}
              {...register('residentData.birthMonth', {
                required: useEnglish
                  ? 'Month is required'
                  : '生年月日(月)が未選択です',
              })}
            >
              <option value="">{useEnglish ? 'Month' : '月'}</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {useEnglish ? m : `${m}月`}
                </option>
              ))}
            </select>
            {errors.residentData?.birthMonth && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-2">
                !
              </span>
            )}
          </div>
          {/* 日 */}
          <div className="relative w-1/3">
            <select
              className={`${selectStyles} ${errors.residentData?.birthDay ? 'border-red-500' : ''}`}
              {...register('residentData.birthDay', {
                required: useEnglish
                  ? 'Day is required'
                  : '生年月日(日)が未選択です',
              })}
            >
              <option value="">{useEnglish ? 'Day' : '日'}</option>
              {days.map((d) => (
                <option key={d} value={d}>
                  {useEnglish ? d : `${d}日`}
                </option>
              ))}
            </select>
            {errors.residentData?.birthDay && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-2">
                !
              </span>
            )}
          </div>
        </div>
        {(errors.residentData?.birthYear ||
          errors.residentData?.birthMonth ||
          errors.residentData?.birthDay) && (
          <div className="text-red-500 text-sm mt-1">
            {errors.residentData.birthYear?.message?.toString() ||
              errors.residentData.birthMonth?.message?.toString() ||
              errors.residentData.birthDay?.message?.toString()}
          </div>
        )}
      </div>

      {/* 性別 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Gender' : '性別'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1 flex space-x-4">
          <label className="flex items-center text-sm">
            <input
              type="radio"
              value="male"
              className="mr-2"
              {...register('residentData.ResidentGender', {
                required: useEnglish
                  ? 'Gender is required'
                  : '性別を選択してください',
              })}
            />
            {useEnglish ? 'Male' : '男性'}
          </label>
          <label className="flex items-center text-sm">
            <input
              type="radio"
              value="female"
              className="mr-2"
              {...register('residentData.ResidentGender', {
                required: useEnglish
                  ? 'Gender is required'
                  : '性別を選択してください',
              })}
            />
            {useEnglish ? 'Female' : '女性'}
          </label>
        </div>
        {errors.residentData?.ResidentGender && (
          <div className="text-red-500 text-sm mt-1">
            {errors.residentData.ResidentGender.message?.toString()}
          </div>
        )}
      </div>

      {/* メールアドレス */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Email Address' : 'メールアドレス'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="email"
            placeholder={
              useEnglish ? 'e.g. sample@example.com' : '例: sample@example.com'
            }
            className={`${inputStyles} ${
              errors.residentData?.email ? 'border-red-500' : ''
            }`}
            {...register('residentData.email', {
              required: useEnglish
                ? 'Email is required'
                : 'メールアドレスが未入力です',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: useEnglish
                  ? 'Invalid email format'
                  : 'メールアドレスの形式が正しくありません',
              },
              validate: (value) => validateCustomEmail(value, useEnglish),
            })}
          />
          {errors.residentData?.email && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
              !
            </span>
          )}
        </div>
        {errors.residentData?.email && (
          <div className="text-red-500 text-sm mt-1 whitespace-pre-line">
            {errors.residentData.email.message?.toString()}
          </div>
        )}
      </div>

      {/* 電話番号 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Phone Number' : '電話番号'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Controller
            name="residentData.phoneNumber"
            control={control}
            rules={{
              required: useEnglish
                ? 'Phone Number is required'
                : '電話番号が未入力です',
            }}
            render={({ field }) => <PhoneCodeSelect {...field} />}
          />
          {errors.residentData?.phoneNumber && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.residentData.phoneNumber.message?.toString()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
